import React, { useState, useEffect, useRef } from 'react';
import Card from '../common/Card';
import MiniLoader from '../common/MiniLoader';
import CopyButton from '../common/CopyButton';
import { useContracts } from '../../hooks/useContract';
import { useNotify } from '../../context/NotificationContext';
import { CONTRACTS } from '../../config/contracts';
import { CURRENT_NETWORK } from '../../config/network';
import { shortenAddress } from '../../utils/wallet';
import { formatUsdt } from '../../utils/format';
import { parseError } from '../../utils/errors';
import { ethers } from 'ethers';

// Owner-only card showing full contract wiring health check + linking tools.
const ContractStatus = () => {
  const { logic, storage, usdt } = useContracts();
  const notify = useNotify();

  const [checks, setChecks] = useState({
    logicAuthorized: null,
    logicPointsToStorage: null,
    logicPrimaryPointer: null,
    usdtSymbol: null,
    usdtBalance: null,
    routerPool: null,
    owner: null,
    admin: null,
    logicOwner: null,
    logicAdmin: null,
    logicSAddress: null
  });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  // Manual link inputs
  const [linkAddr, setLinkAddr] = useState('');
  const [authAddr, setAuthAddr] = useState('');
  const [authGrant, setAuthGrant] = useState(true);

  const runIdRef = useRef(0);

  const load = async () => {
    const myRun = ++runIdRef.current;
    setLoading(true);
    try {
      const [
        storageAuthCheck,
        logicStorageAddr,
        logicPrimaryPointer,
        usdtSymbol,
        usdtBal,
        pool,
        sOwner,
        sAdmin,
        lOwner,
        lAdmin
      ] = await Promise.all([
        storage.isLogic(CONTRACTS.GlobalLogic).catch(() => null),
        logic.s().catch(() => null),
        storage.logicContract().catch(() => null),
        usdt.symbol().catch(() => null),
        usdt.balanceOf(CONTRACTS.GlobalStorage).catch(() => null),
        storage.routerPool().catch(() => null),
        storage.owner().catch(() => null),
        storage.admin().catch(() => null),
        logic.owner().catch(() => null),
        logic.admin().catch(() => null)
      ]);

      if (myRun !== runIdRef.current) return;

      setChecks({
        logicAuthorized: storageAuthCheck,
        logicPointsToStorage: logicStorageAddr && logicStorageAddr.toLowerCase() === CONTRACTS.GlobalStorage.toLowerCase(),
        logicPrimaryPointer,
        usdtSymbol,
        usdtBalance: usdtBal,
        routerPool: pool,
        owner: sOwner,
        admin: sAdmin,
        logicOwner: lOwner,
        logicAdmin: lAdmin,
        logicSAddress: logicStorageAddr
      });
    } catch (_) { /* ignore */ }
    finally {
      if (myRun === runIdRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  const doTx = async (label, txPromise) => {
    setBusy(label);
    const l = notify.loading(`${label}...`);
    try {
      const tx = await txPromise;
      notify.dismiss(l);
      const m = notify.loading('Confirming...');
      await tx.wait();
      notify.dismiss(m);
      notify.success(`${label} — done`);
      await load();
    } catch (err) {
      notify.dismiss(l);
      notify.error(parseError(err));
    } finally {
      setBusy(null);
    }
  };

  const linkCurrentLogic = () => {
    doTx('Linking logic contract', storage.setLogicContract(CONTRACTS.GlobalLogic));
  };

  const linkManualLogic = () => {
    if (!ethers.utils.isAddress(linkAddr)) { notify.error('Invalid address'); return; }
    doTx('Setting logic contract', storage.setLogicContract(linkAddr));
  };

  const toggleAuth = () => {
    if (!ethers.utils.isAddress(authAddr)) { notify.error('Invalid address'); return; }
    doTx(
      authGrant ? 'Granting logic auth' : 'Revoking logic auth',
      storage.setLogicAuth(authAddr, authGrant)
    );
  };

  const StatusDot = ({ ok }) => (
    <span className={`status-dot ${ok === true ? 'ok' : ok === false ? 'bad' : 'unknown'}`}></span>
  );

  const StatusRow = ({ label, value, addr, ok, mono }) => (
    <div className="status-row">
      <div className="status-label">
        <StatusDot ok={ok} />
        {label}
      </div>
      <div className={'status-value ' + (mono ? 'font-mono' : '')}>
        {value}
        {addr && <CopyButton text={addr} label="" copiedLabel="✓" />}
      </div>
    </div>
  );

  const linkOk = checks.logicAuthorized === true && checks.logicPointsToStorage === true;
  const ownerMatch = checks.owner && checks.logicOwner && checks.owner.toLowerCase() === checks.logicOwner.toLowerCase();
  const adminMatch = checks.admin && checks.logicAdmin && checks.admin.toLowerCase() === checks.logicAdmin.toLowerCase();

  const needsAuth = checks.logicAuthorized === false;
  const storageMismatch = checks.logicPointsToStorage === false;
  const primaryPointerMismatch = checks.logicPrimaryPointer &&
    checks.logicPrimaryPointer !== ethers.constants.AddressZero &&
    checks.logicPrimaryPointer.toLowerCase() !== CONTRACTS.GlobalLogic.toLowerCase();

  return (
    <>
      <Card
        title="Contract Status"
        subtitle={CURRENT_NETWORK.name + ' network health check'}
        glow
        right={<span className={`badge ${linkOk ? 'badge-success' : 'badge-error'}`}>{linkOk ? 'Linked' : 'Needs Attention'}</span>}
      >
        {loading ? (
          <div className="text-center" style={{ padding: 24 }}><MiniLoader /></div>
        ) : (
          <>
            <div className="status-list">
              <StatusRow
                label="Logic contract"
                value={shortenAddress(CONTRACTS.GlobalLogic, 6)}
                addr={CONTRACTS.GlobalLogic}
                ok={!!CONTRACTS.GlobalLogic && CONTRACTS.GlobalLogic !== ethers.constants.AddressZero}
                mono
              />
              <StatusRow
                label="Storage contract"
                value={shortenAddress(CONTRACTS.GlobalStorage, 6)}
                addr={CONTRACTS.GlobalStorage}
                ok={!!CONTRACTS.GlobalStorage && CONTRACTS.GlobalStorage !== ethers.constants.AddressZero}
                mono
              />
              <StatusRow
                label="USDT token"
                value={`${shortenAddress(CONTRACTS.USDT, 6)}${checks.usdtSymbol ? ' · ' + checks.usdtSymbol : ''}`}
                addr={CONTRACTS.USDT}
                ok={!!checks.usdtSymbol}
                mono
              />

              <div className="status-divider"></div>

              <StatusRow
                label="Logic → authorized in Storage"
                value={checks.logicAuthorized ? 'Yes' : 'NO — needs linking'}
                ok={checks.logicAuthorized === true}
              />
              <StatusRow
                label="Storage primary logic pointer"
                value={checks.logicPrimaryPointer ? shortenAddress(checks.logicPrimaryPointer, 6) : '-'}
                addr={checks.logicPrimaryPointer}
                ok={!primaryPointerMismatch && checks.logicPrimaryPointer && checks.logicPrimaryPointer !== ethers.constants.AddressZero}
                mono
              />
              <StatusRow
                label="Logic → points to Storage"
                value={checks.logicPointsToStorage ? 'Yes' : `NO — logic points to ${shortenAddress(checks.logicSAddress || '-')}`}
                ok={checks.logicPointsToStorage === true}
              />
              <StatusRow
                label="Owner match (Logic/Storage)"
                value={ownerMatch ? 'Yes' : 'Mismatch'}
                ok={ownerMatch}
              />
              <StatusRow
                label="Admin match (Logic/Storage)"
                value={adminMatch ? 'Yes' : 'Mismatch'}
                ok={adminMatch}
              />

              <div className="status-divider"></div>

              <StatusRow
                label="Storage USDT balance"
                value={checks.usdtBalance ? `${formatUsdt(checks.usdtBalance)} USDT` : '-'}
                ok={checks.usdtBalance !== null}
              />
              <StatusRow
                label="Router pool"
                value={checks.routerPool ? `${formatUsdt(checks.routerPool)} USDT` : '-'}
                ok={checks.routerPool !== null}
              />
            </div>

            {/* Warnings + Fix buttons */}
            {(needsAuth || primaryPointerMismatch) && !storageMismatch && (
              <div className="fix-banner fix-banner-warning">
                <div className="fix-banner-icon">⚠</div>
                <div className="fix-banner-body">
                  <div className="fix-banner-title">Logic contract not linked</div>
                  <div className="fix-banner-desc">
                    The configured Logic contract ({shortenAddress(CONTRACTS.GlobalLogic)}) is not
                    authorized in Storage. Click "Link Now" to grant it authorization + set as primary.
                  </div>
                  <button
                    className="btn btn-primary btn-sm mt-md"
                    onClick={linkCurrentLogic}
                    disabled={busy !== null}
                  >
                    {busy === 'Linking logic contract' ? <><MiniLoader /> Linking</> : '🔗 Link Now'}
                  </button>
                </div>
              </div>
            )}

            {storageMismatch && (
              <div className="fix-banner fix-banner-error">
                <div className="fix-banner-icon">🚨</div>
                <div className="fix-banner-body">
                  <div className="fix-banner-title">Logic points to different Storage</div>
                  <div className="fix-banner-desc">
                    The Logic contract is hard-coded to a different Storage
                    ({shortenAddress(checks.logicSAddress)}) than the configured one
                    ({shortenAddress(CONTRACTS.GlobalStorage)}). Since Logic's storage pointer is
                    set at construction and cannot be changed, you must either:
                    <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                      <li>Redeploy the Logic contract pointing to the correct Storage, then link it below</li>
                      <li>OR update the Storage address in your config to match ({shortenAddress(checks.logicSAddress)})</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {!ownerMatch && checks.owner && checks.logicOwner && (
              <div className="fix-banner fix-banner-warning">
                <div className="fix-banner-icon">⚠</div>
                <div className="fix-banner-body">
                  <div className="fix-banner-title">Owner mismatch</div>
                  <div className="fix-banner-desc">
                    Storage owner is {shortenAddress(checks.owner)} but Logic owner is {shortenAddress(checks.logicOwner)}.
                    This is only cosmetic if you intended it, but usually both should match.
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </Card>

      {/* Manual linking tools */}
      <Card title="Manual Linking Tools" subtitle="For advanced setups or multiple logic contracts">
        <div className="linking-block">
          <div className="linking-block-title">Set Primary Logic Contract</div>
          <div className="linking-block-desc">
            Sets a new logic contract as the primary. The old primary is revoked and the new one is granted authorization.
          </div>
          <div className="flex gap-sm mt-md">
            <input
              className="input font-mono"
              placeholder="0x..."
              value={linkAddr}
              onChange={(e) => setLinkAddr(e.target.value)}
            />
            <button
              className="btn btn-primary btn-sm"
              onClick={linkManualLogic}
              disabled={busy !== null || !linkAddr}
            >
              {busy === 'Setting logic contract' ? <MiniLoader /> : 'Set as Primary'}
            </button>
          </div>
        </div>

        <div className="linking-block">
          <div className="linking-block-title">Grant / Revoke Auth Only</div>
          <div className="linking-block-desc">
            Authorize (or revoke) another logic contract without changing the primary pointer.
            Useful when running a secondary admin logic contract alongside the main one.
          </div>
          <div className="flex gap-sm mt-md">
            <input
              className="input font-mono"
              placeholder="0x..."
              value={authAddr}
              onChange={(e) => setAuthAddr(e.target.value)}
            />
            <select
              className="input"
              style={{ maxWidth: 120 }}
              value={authGrant ? '1' : '0'}
              onChange={(e) => setAuthGrant(e.target.value === '1')}
            >
              <option value="1">Grant</option>
              <option value="0">Revoke</option>
            </select>
            <button
              className="btn btn-primary btn-sm"
              onClick={toggleAuth}
              disabled={busy !== null || !authAddr}
            >
              {busy && busy.includes('auth') ? <MiniLoader /> : 'Apply'}
            </button>
          </div>
        </div>

        <div className="text-muted mt-md" style={{ fontSize: 12, padding: '10px 12px', background: 'rgba(5,6,26,0.4)', borderRadius: 10 }}>
          💡 Any authorized logic contract can call state-changing functions on Storage.
          The "primary" pointer is kept for backwards compatibility. Use "Grant / Revoke Auth"
          for fine-grained control over multiple logic contracts.
        </div>
      </Card>
    </>
  );
};

export default ContractStatus;
